import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonPhoneInput } from '@/components/CommonForm/CommonPhoneInput';
import { CommonServiceSelect } from '@/components/CommonForm/CommonServiceSelect';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents } from '@/tools/forms';
import { withSuspense } from '@/tools/withSuspense';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { faCircleCheck, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RichText } from '@lamesarv-sdk/components';
import { navigate, parsePhone } from '@lamesarv-sdk/tools';
import { IMetaData } from '@lamesarv-sdk/types';
import { Fragment, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { BaseButton, BaseOptInCheckbox, BaseSubmit, BaseSubscribeCheckbox } from '../base';
import { IFormProps, IFormResetterProps } from '../types';
import { IFormSchema, validationSchema } from './schema';

import * as FormClasses from '../classes';

const formCaptchaErrorMessage = () => {
  return (
    <div className="w-full flex flex-col gap-2 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="font-bold">Form Validation failed.</div>
      <div className="w-full">
        Please Call us at{' '}
        <a href="tel:855-731-2507" className="decoration-dashed underline underline-offset-4">
          <FontAwesomeIcon icon={faPhone} className="size-3.5 inline mr-1" />
          <span>855-731-2507</span>
        </a>{' '}
        for further assistance.
      </div>
    </div>
  );
};

enum FormPanel {
  rv = 'rv-information',
  personal = 'personal-information',
}

const formStyles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-5 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-2 group-focus-within:top-2 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-2 top-2 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-5 rounded-md focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-8 focus:pb-2',
  inputWithValue: 'px-3 pt-8 pb-2',
  button:
    'capitalize rounded-md bg-blue-500 text-white border-none hover:bg-blue-400 hover:text-white hover:border-none',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export interface IFormServiceAppointmentProps extends IFormProps {
  locationsOptionSet?: IDynamicsOptionSet;
  metadata?: IMetaData;
  first_panel?: string;
  panel_rv_information_title?: string;
  panel_personal_information_title?: string;
  next_button_text?: string;
  message_success_title?: string;
  success_button_text?: string;
  classes_next_button?: string;
  classes_success_title?: string;
  classes_success_text?: string;
  classes_success_button?: string;
}

export interface IFormPanelNavigationProps {
  stepNext: boolean;
  setStepNext: (value: boolean) => void;
  panelFirst: { title: string; fields: string[] };
  panelSecond: { title: string; fields: string[] };
}

const FormPanelNavigation = (props: IFormPanelNavigationProps) => {
  const { trigger } = useFormContext();
  const { stepNext, setStepNext } = props;

  return (
    <div className="flex flex-row justify-center gap-4">
      <div className="text-right">
        <div
          onClick={() => {
            setStepNext(false);
          }}
          className="flex flex-row items-center cursor-pointer text-blue-600 text-[14px] md:text-[16px] font-semibold"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="w-8" />
          <span>{props.panelFirst.title}</span>
        </div>
      </div>
      <div className="text-left">
        <div
          onClick={async () => {
            const panelValidates = await trigger(props.panelFirst.fields);
            if (panelValidates) {
              setStepNext(true);
            }
          }}
          className={twMerge(
            'flex flex-row items-center cursor-pointer text-[14px] md:text-[16px] font-semibold',
            stepNext ? 'text-blue-600' : 'text-black-300 font',
          )}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="w-8" />
          <span>{props.panelSecond.title}</span>
        </div>
      </div>
    </div>
  );
};

export interface IFormPanelProps extends IFormServiceAppointmentProps {
  stepNext: boolean;
  setStepNext: (value: boolean) => void;
  panelFields: string[];
  isFirstPanel?: boolean;
}

const FormPanelRV = (props: IFormPanelProps) => {
  const { trigger } = useFormContext();

  const isVisible = (!!props.isFirstPanel && !props.stepNext) || (!props.isFirstPanel && props.stepNext);

  return (
    <div className={twMerge('hidden flex-col', props.classes_content, isVisible && 'flex')}>
      <div className="flex flex-row flex-wrap -mx-2">
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="number"
            label="Year"
            name="year"
            customOptions={{ min: 0, step: 1 }}
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              formStyles.input,
              props.classes_input,
            )}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="text"
            label="Make"
            name="make"
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="text"
            label="Model"
            name="model"
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="text"
            label="VIN"
            name="vin"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full">
          <CommonTextarea
            label="Comments"
            name="comments"
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
      </div>
      <div className="w-full">
        <div className="mt-2 text-[14px] md:text-[18px]">
          {!!props.isFirstPanel && (
            <BaseButton
              {...props}
              text={props.next_button_text || 'Next'}
              nameSlugForm="form-contact-sell"
              nameSlugButton="next"
              onClick={async () => {
                const panelValidates = await trigger(props.panelFields);
                if (panelValidates) {
                  props.setStepNext(true);
                }
              }}
              styleClasses={twMerge(
                FormClasses.buttonNormalClasses,
                formStyles.button,
                'w-full',
                props.classes_next_button,
              )}
            />
          )}
          {!props.isFirstPanel && (
            <BaseSubmit
              {...props}
              formNameSlug="form-contact-sell"
              styleClasses={twMerge(FormClasses.buttonNormalClasses, formStyles.button, 'w-full', props.classes_submit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const FormPanelPersonal = (props: IFormPanelProps) => {
  const { trigger } = useFormContext();

  const isVisible = (!!props.isFirstPanel && !props.stepNext) || (!props.isFirstPanel && props.stepNext);

  return (
    <div className={twMerge('hidden flex-col', props.classes_content, isVisible && 'flex')}>
      <div className="flex flex-row flex-wrap -mx-2">
        <div className="my-2 px-2 w-full">
          <CommonServiceSelect
            label="Preferred Service Center"
            name="serviceCenter"
            servicesOptionSet={props.servicesOptionSet}
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="text"
            label="First Name"
            name="firstName"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="text"
            label="Last Name"
            name="lastName"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full">
          <CommonPhoneInput
            label="Phone"
            name="phone"
            isRequired
            isFloatingLabel
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, 'pt-4 pb-5', props.classes_input)}
            classInputWithValue={twMerge(formStyles.inputWithValue, 'pt-7')}
          />
        </div>
        <div className="my-2 px-2 w-full">
          <CommonInput
            type="email"
            label="Email"
            name="email"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
      </div>
      <div className="w-full">
        {props.display_subscribe_checkbox && (
          <div className="flex flex-row py-2 text-left">
            <BaseSubscribeCheckbox htmlContent={props.custom_subscribe_text} />
          </div>
        )}
        {props.display_sms_checkbox && (
          <div className="flex flex-row py-2 text-left">
            <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
          </div>
        )}
        <div className="mt-2 text-[14px] md:text-[18px]">
          {!!props.isFirstPanel && (
            <BaseButton
              {...props}
              text={props.next_button_text || 'Next'}
              nameSlugForm="form-contact-sell"
              nameSlugButton="next"
              onClick={async () => {
                const panelValidates = await trigger(props.panelFields);
                if (panelValidates) {
                  props.setStepNext(true);
                }
              }}
              styleClasses={twMerge(
                FormClasses.buttonNormalClasses,
                formStyles.button,
                'w-full',
                props.classes_next_button,
              )}
            />
          )}
          {!props.isFirstPanel && (
            <BaseSubmit
              {...props}
              formNameSlug="form-contact-sell"
              styleClasses={twMerge(FormClasses.buttonNormalClasses, formStyles.button, 'w-full', props.classes_submit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const FormServiceAppointmentSteps = withSuspense((props: IFormServiceAppointmentProps) => {
  const [stepNext, setStepNext] = useState<boolean>(false);

  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.service) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      {
        DataverseFieldName: 'lmrv_service_center',
        DataverseFieldValue:
          String(props.servicesOptionSet.find((item) => item.Label === formInput.serviceCenter)?.DataverseValue) || '',
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: formInput.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: formInput.lastName },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: formInput.email },
      { DataverseFieldName: 'lmrv_vehicleyear', DataverseFieldValue: formInput.year || '' },
      { DataverseFieldName: 'lmrv_make', DataverseFieldValue: formInput.make || '' },
      { DataverseFieldName: 'lmrv_model', DataverseFieldValue: formInput.model || '' },
      { DataverseFieldName: 'lmrv_vin', DataverseFieldValue: formInput.vin || '' },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: formInput.comments || '' },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (formInput.subscribe || formInput.smsOptIn) {
      const topicChannels = [formInput.subscribe ? 'Email' : undefined, formInput.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    await sendForm({
      formKey: 'SERVICE',
      formName: props.custom_name || 'Form Service Appointment',
      mappings,
    });
  };

  const onSuccessClick = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const wasSuccessful = !isLoading && !hasError && submitted;

  const panelFieldsRv = ['year', 'make', 'model', 'vin', 'comments'];
  const panelFieldsPersonal = ['serviceCenter', 'firstName', 'lastName', 'email', 'phone'];

  const panelFirst = {
    title: props.first_panel === FormPanel.rv ? 'RV Information' : 'Personal Information',
    fields: props.first_panel === FormPanel.rv ? panelFieldsRv : panelFieldsPersonal,
  };

  const panelSecond = {
    title: props.first_panel === FormPanel.rv ? 'Personal Information' : 'RV Information',
    fields: props.first_panel === FormPanel.rv ? panelFieldsPersonal : panelFieldsRv,
  };

  return (
    <CommonForm
      id={props.custom_id || 'formServiceAppointment'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-service-appointment` : 'form-service-appointment'}
      customCaptchaErrorMessage={formCaptchaErrorMessage}
    >
      <CommonHidden name="formId" value="34" />
      <CommonHidden name="formType" value="service" />
      <CommonHidden name="leadType" value="service" />
      <CommonHidden name="leadSource" value="lamesarv.com" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      <div className={twMerge('w-full flex flex-col gap-4 p-2 text-center', props.classes_header)}>
        {props.title && (
          <div
            className={twMerge('text-[24px] md:text-[34px] font-bold leading-none text-blue-600', props.classes_title)}
          >
            {props.title}
          </div>
        )}
        {props.description && (
          <div className={twMerge('text-[14px] md:text-[16px] text-blue-600', props.classes_description)}>
            <RichText content={props.description} />
          </div>
        )}
      </div>
      {isLoading && (
        <div
          className={
            'flex items-center gap-2 w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 rounded-md shadow-md'
          }
          role="alert"
        >
          <FontAwesomeIcon icon={faClock} className="w-8" />
          <span>Sending...</span>
        </div>
      )}
      {hasError && (
        <div
          className={'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 rounded-md'}
          role="alert"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
          <span>An error has occurred, please try again later or contact us using alternative methods.</span>
        </div>
      )}
      <div
        className={twMerge(
          'relative w-full flex flex-col gap-4 p-6 md:p-8 text-center justify-center bg-white md:border border-black-300 rounded-lg',
          props.classes_container,
        )}
      >
        <FormPanelNavigation
          panelFirst={panelFirst}
          panelSecond={panelSecond}
          stepNext={stepNext}
          setStepNext={setStepNext}
        />
        {props.first_panel === FormPanel.rv && (
          <Fragment>
            <FormPanelRV
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsRv}
              isFirstPanel
            />
            <FormPanelPersonal
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsPersonal}
            />
          </Fragment>
        )}
        {props.first_panel === FormPanel.personal && (
          <Fragment>
            <FormPanelPersonal
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsPersonal}
              isFirstPanel
            />
            <FormPanelRV {...props} stepNext={stepNext} setStepNext={setStepNext} panelFields={panelFieldsRv} />
          </Fragment>
        )}
        {/* Success Screen */}
        <div
          className={twMerge(
            'hidden absolute inset-1 bg-white flex-col items-center justify-center',
            props.classes_content,
            !!wasSuccessful && 'flex',
          )}
        >
          <div
            className="w-full flex flex-col gap-4 px-10"
            data-tag-group="form-success-label"
            data-tag={
              props.data_tag
                ? `${props.data_tag}--form-service-appointment-success-label`
                : 'form-service-appointment-success-label'
            }
          >
            <div className={twMerge('px-14 text-[14px] md:text-[18px] font-bold', props.classes_success_title)}>
              {props.message_success_title}
            </div>
            <div className={twMerge('text-[14px] md:text-[16px]', props.classes_success_text)}>
              <RichText content={props.message_success} />
            </div>
            <div className="px-14 text-[14px] md:text-[18px]">
              <BaseButton
                {...props}
                text={props.success_button_text || 'Thank You'}
                nameSlugForm="form-contact-sell"
                nameSlugButton="cta"
                onClick={onSuccessClick}
                styleClasses={twMerge(
                  FormClasses.buttonNormalClasses,
                  formStyles.button,
                  'w-full',
                  props.classes_success_button,
                )}
                forceDisabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
